import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@modules/App/redux/store';
import { ClientProjectData } from '@modules/EvaluationModule/interfaces/ClientProjectData';
import { ClientContactData } from '@modules/EvaluationModule/interfaces/ClientSubProjectData';
import { ClientWorkstreamData } from '@modules/EvaluationModule/interfaces/ClientWorkstreamData';
import { ClientSubProjectFilterOptions } from '@modules/EvaluationModule/interfaces/ClientSubProjectFilterOptions';
import { DataState } from '@shared/enums/DataState';
import { AsyncData } from '@shared/interfaces/asyncData';
import { FeedbackGiver } from '../enums/ContactFilterOptions';
import { CacheEntry } from './types';

const IDLE_CACHE_ENTRY: CacheEntry<never[]> = { data: null, state: DataState.Idle, fetchedAt: null };

export const networkErrorSelector = (state: RootState): string | null =>
  state.evaluation.clientContactPage.clientSubProjectNetworkError;

export const editClientDataStateSelector = (state: RootState): DataState =>
  state.evaluation.clientContactPage.editClientDataState;

export const clientContactDataSelector = (state: RootState): AsyncData<ClientProjectData[]> =>
  state.evaluation.clientContactPage.clientContactData;

export const clientWorkstreamsDataSelector =
  (clientId: string | null) =>
  (state: RootState): CacheEntry<ClientWorkstreamData[]> => {
    if (!clientId) return IDLE_CACHE_ENTRY;
    return state.evaluation.clientContactPage.clientWorkstreamsData[clientId] ?? IDLE_CACHE_ENTRY;
  };

export const clientSubProjectFilterOptionsSelector = (state: RootState): ClientSubProjectFilterOptions =>
  state.evaluation.clientContactPage.uniqueFilterOptions;

export const clientSubProjectFilterSelector = (state: RootState): ClientSubProjectFilterOptions =>
  state.evaluation.clientContactPage.clientFilters;

export const clientWorkstreamContactsDataSelector =
  (payload: { workstreamId: string | null; clientId: string | null }) =>
  (state: RootState): CacheEntry<ClientContactData[]> => {
    const { workstreamId } = payload;
    if (workstreamId) {
      const entry = state.evaluation.clientContactPage.workstreamContactsData[workstreamId];
      if (entry) return entry;
    }
    return IDLE_CACHE_ENTRY;
  };

const workstreamContactsDataMapSelector = (state: RootState) =>
  state.evaluation.clientContactPage.workstreamContactsData;

const includeClosedWorkstreamsSelector = (state: RootState) =>
  state.evaluation.clientContactPage.uniqueFilterOptions.includeClosedWorkstreams;

const showFormerContactsSelector = (state: RootState) =>
  state.evaluation.clientContactPage.uniqueFilterOptions.showFormerContacts;

// F-S2: this used to run — and allocate a brand-new object — on every render that read it,
// which meant every consumer re-rendered too. createSelector memoizes on its inputs' identity.
export const computedFilterOptionsSelector = createSelector(
  [workstreamContactsDataMapSelector, includeClosedWorkstreamsSelector, showFormerContactsSelector],
  (wsData, includeClosedWorkstreams, showFormerContacts): ClientSubProjectFilterOptions => {
    const status: string[] = [];
    const typeOfClient: string[] = [];
    const clientTitle: string[] = [];
    const contactOwner: string[] = [];
    const contactLocation: string[] = [];
    const locationArea: string[] = [];

    Object.values(wsData).forEach((entry) => {
      if (!entry.data) return;
      entry.data.forEach((contact: ClientContactData) => {
        if (contact.rmContactStatus && !status.includes(contact.rmContactStatus)) status.push(contact.rmContactStatus);
        if (contact.rmTypeOfContact && !typeOfClient.includes(contact.rmTypeOfContact))
          typeOfClient.push(contact.rmTypeOfContact);
        if (contact.title && !clientTitle.includes(contact.title)) clientTitle.push(contact.title);
        if (contact.contactOwner && !contactOwner.includes(contact.contactOwner))
          contactOwner.push(contact.contactOwner);
        if (contact.location && contact.location.toLowerCase() !== 'na' && !contactLocation.includes(contact.location))
          contactLocation.push(contact.location);
        if (
          contact.locationArea &&
          contact.location &&
          contact.location.toLowerCase() !== 'na' &&
          !locationArea.includes(contact.locationArea)
        )
          locationArea.push(contact.locationArea);
      });
    });

    return {
      status,
      typeOfClient,
      clientTitle,
      includeClosedWorkstreams,
      // ROCKY-27762: this selector is typed with the same interface as the live filter, so a
      // new filter key has to be carried here too. It is a toggle, not a computed option list.
      showFormerContacts,
      contactOwner,
      contactLocation,
      locationArea,
      feedbackGiver: Object.values(FeedbackGiver),
    };
  }
);
